import { useEffect, useState, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { apiService } from '../services/api'
import VideoPlayer from '../components/VideoPlayer'
import MarkerCarousel from '../components/MarkerCarousel'
import { ChevronRight } from 'lucide-react'

export default function VideoPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [paper, setPaper] = useState(null)
  const [papers, setPapers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [currentTime, setCurrentTime] = useState(0)
  const playerRef = useRef(null)

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true)
        // Load current paper
        const paperData = await apiService.getPaper(id)
        setPaper(paperData.data)

        // Load all papers for counting
        const allPapers = await apiService.getPapers()
        setPapers(allPapers.data)
      } catch (err) {
        setError(err.message)
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, [id])

  // Handle marker click - seek to time
  const handleMarkerClick = (timeSeconds) => {
    if (playerRef.current) {
      playerRef.current.currentTime(timeSeconds)
      playerRef.current.play()
    }
  }

  // Count related videos
  const countRelatedVideos = (field, value) => {
    if (!papers.length || !value) return 0

    return papers.filter(p => {
      if (field === 'author') {
        return p.author1 === value && p.id !== paper.id
      }
      if (field === 'kategorie') {
        if (Array.isArray(p.kategorie)) {
          return p.kategorie.includes(value) && p.id !== paper.id
        }
        return p.kategorie === value && p.id !== paper.id
      }
      if (field === 'ort') {
        return p.ort === value && p.id !== paper.id
      }
      if (field === 'jahr') {
        return p.jahr === value && p.id !== paper.id
      }
      return false
    }).length
  }

  // Create search link
  const handleRelatedClick = (searchTerm) => {
    navigate(`/?search=${encodeURIComponent(searchTerm)}`)
  }

  if (loading) return <div className="text-center py-12">Lädt Video...</div>
  if (error) return <div className="text-center py-12 text-red-600">Fehler: {error}</div>
  if (!paper) return <div className="text-center py-12">Video nicht gefunden</div>

  // Get kategorie as string
  const kategoriString = Array.isArray(paper.kategorie)
    ? paper.kategorie[0]
    : paper.kategorie

  // Konferenztyp-Kürzel
  const KONFERENZTYP_KURZEL = {
    'Bundeskonferenz': 'BK',
    'Regionalkonferenz': 'RK',
    'Workshop': 'WS',
    'Fortbildung': 'FB',
    'Andere': '—',
  }
  const kuerzel = KONFERENZTYP_KURZEL[paper.konferenztyp] ?? null

  // Count related
  const authorCount = countRelatedVideos('author', paper.author1)
  const kategorieCount = countRelatedVideos('kategorie', kategoriString)
  const ortCount = countRelatedVideos('ort', paper.ort)
  const jahCount = countRelatedVideos('jahr', paper.jahr)

  return (
    <div className="w-full bg-white">
      {/* Breadcrumb */}
      <div className="px-4 md:px-6 lg:px-8 py-4 border-b border-gray-200 bg-gray-50">
        <div className="max-w-7xl mx-auto flex items-center gap-2 text-sm text-gray-600">
          <button
            onClick={() => navigate('/')}
            className="hover:text-green-600 transition"
          >
            Home
          </button>
          <ChevronRight size={16} className="text-gray-400" />
          <span className="text-gray-900 font-medium truncate">{paper.title}</span>
        </div>
      </div>

      {/* Header Section */}
      <div className="px-4 md:px-6 lg:px-8 py-6 border-b border-gray-200">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-3xl md:text-4xl font-bold mb-3">{paper.title}</h1>
          <div className="flex flex-wrap items-center gap-2 text-gray-600 text-sm md:text-base mb-6">
            <span>{paper.author1}</span>
            <span className="text-gray-400">|</span>
            <span>{Array.isArray(paper.kategorie) ? paper.kategorie.join(', ') : paper.kategorie}</span>
            <span className="text-gray-400">|</span>
            <span>{paper.ort}</span>
            {kuerzel && (<><span className="text-gray-400">|</span><span>{kuerzel}</span></>)}
            <span className="text-gray-400">|</span>
            <span>{paper.jahr}</span>
          </div>

          {/* Related Videos Links */}
          <div className="flex flex-wrap gap-2">
            {authorCount > 0 && (
              <button
                onClick={() => handleRelatedClick(paper.author1)}
                className="px-3 py-1 bg-green-100 hover:bg-green-200 text-green-800 text-sm rounded transition cursor-pointer"
              >
                Weitere Videos von {paper.author1} ({authorCount} weitere)
              </button>
            )}

            {kategorieCount > 0 && (
              <button
                onClick={() => handleRelatedClick(kategoriString)}
                className="px-3 py-1 bg-green-100 hover:bg-green-200 text-green-800 text-sm rounded transition cursor-pointer"
              >
                Weitere {kategoriString}-Videos ({kategorieCount} weitere)
              </button>
            )}

            {ortCount > 0 && (
              <button
                onClick={() => handleRelatedClick(paper.ort)}
                className="px-3 py-1 bg-green-100 hover:bg-green-200 text-green-800 text-sm rounded transition cursor-pointer"
              >
                Weitere Videos aus {paper.ort} ({ortCount} weitere)
              </button>
            )}

            {jahCount > 0 && (
              <button
                onClick={() => handleRelatedClick(String(paper.jahr))}
                className="px-3 py-1 bg-green-100 hover:bg-green-200 text-green-800 text-sm rounded transition cursor-pointer"
              >
                Videos aus {paper.jahr} ({jahCount} weitere)
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Video Player Section - Fullwidth */}
      <div className="w-full bg-white py-6">
        <VideoPlayer
          ref={playerRef}
          paper={paper}
          onTimeUpdate={setCurrentTime}
        />
      </div>

      {/* Marker Carousel Section */}
      <div className="px-4 md:px-6 lg:px-8 py-8 bg-gray-50 border-t border-gray-200">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-sm text-gray-500 mb-6">Folien ({paper.markers?.length || 0})</h2>
          <MarkerCarousel
            markers={paper.markers || []}
            currentTime={currentTime}
            onMarkerClick={handleMarkerClick}
          />
        </div>
      </div>
    </div>
  )
}